import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Radio,
  RadioGroup,
  Text,
} from "@chakra-ui/react";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";
import {
  EntityProfileContactCardShell,
  EntityProfileContactDetailsCard,
  EntityProfileContactGrid,
  EntityProfileContactInputField,
  EntityProfileContactPhoneField,
} from "../../../../../entityProfileModal/components/EntityProfileContactCard";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../../entityProfileModal/entityProfileModal.theme";
import AddressAutocompleteInput from "../../../../../office-branch/components/AddressAutocompleteInput";
import { formatCaregiverProfileValue } from "../../../../caregiverProfileModal.utils";
import type { CaregiverEmergencyContact } from "../caregiverEmergencyContact.types";
import {
  formatCaregiverEmergencyContactPhone,
  mapCaregiverEmergencyContactToDraft,
} from "../caregiverEmergencyContact.utils";

export function CaregiverEmergencyContactCard(props: {
  contact: CaregiverEmergencyContact;
  index: number;
}) {
  const { contact } = props;

  return (
    <EntityProfileContactDetailsCard
      columns={2}
      fields={[
        { label: "Name", value: formatCaregiverProfileValue(contact.name) },
        {
          label: "Lives With Patient",
          value: formatCaregiverProfileValue(contact.livesWithPatient),
        },
        { label: "Relationship", value: formatCaregiverProfileValue(contact.relationship) },
        { label: "Phone 1", value: formatCaregiverEmergencyContactPhone(contact.phone1) },
        { label: "Phone 2", value: formatCaregiverEmergencyContactPhone(contact.phone2) },
        { label: "Address", value: formatCaregiverProfileValue(contact.address) },
      ]}
      title={`Emergency Contact #${props.index + 1}`}
    />
  );
}

export function CaregiverEmergencyContactEditorCard(props: {
  contact: CaregiverEmergencyContact;
  index: number;
  isDisabled: boolean;
  showErrors: boolean;
  onChange: (index: number, contact: CaregiverEmergencyContact) => void;
  onRemove: (index: number) => void;
}) {
  const contact = mapCaregiverEmergencyContactToDraft(props.contact);

  const updateField = <TKey extends keyof CaregiverEmergencyContact>(
    key: TKey,
    value: CaregiverEmergencyContact[TKey]
  ) => {
    props.onChange(props.index, { ...contact, [key]: value });
  };

  return (
    <EntityProfileContactCardShell
      isRemoveDisabled={props.isDisabled}
      removeLabel="Remove emergency contact"
      title={`Emergency Contact #${props.index + 1}`}
      variant="editing"
      onRemove={() => props.onRemove(props.index)}
    >
      <EntityProfileContactGrid columns={2}>
        <EntityProfileContactInputField
          errorMessage="Name is required."
          isDisabled={props.isDisabled}
          isInvalid={props.showErrors && contact.name.trim() === ""}
          isRequired={true}
          label="Name"
          value={contact.name}
          onChange={(value) => updateField("name", value)}
        />
        <NullableBooleanField
          isDisabled={props.isDisabled}
          label="Lives With Patient"
          value={contact.livesWithPatient}
          onChange={(value) => updateField("livesWithPatient", value)}
        />
        <EntityProfileContactInputField
          isDisabled={props.isDisabled}
          label="Relationship"
          value={contact.relationship}
          onChange={(value) => updateField("relationship", value)}
        />
        <EntityProfileContactPhoneField
          isDisabled={props.isDisabled}
          isRequired={true}
          label="Phone 1"
          showErrors={props.showErrors}
          value={contact.phone1}
          onChange={(value) => updateField("phone1", value)}
        />
        <EntityProfileContactPhoneField
          isDisabled={props.isDisabled}
          label="Phone 2"
          showErrors={props.showErrors}
          value={contact.phone2}
          onChange={(value) => updateField("phone2", value)}
        />
        <FormControl>
          <FormLabel fontSize="13px" fontWeight={700}>
            Address
          </FormLabel>
          <AddressAutocompleteInput
            addressText={contact.address}
            isDisabled={props.isDisabled}
            value={null}
            onChange={(address) => updateField("address", address)}
            onClear={() => updateField("address", "")}
          />
        </FormControl>
      </EntityProfileContactGrid>
    </EntityProfileContactCardShell>
  );
}

function NullableBooleanField(props: {
  isDisabled: boolean;
  label: string;
  value: boolean | null;
  onChange: (value: boolean | null) => void;
}) {
  return (
    <FormControl>
      <FormLabel fontSize="13px" fontWeight={700}>
        {props.label}
      </FormLabel>
      <RadioGroup
        value={props.value === null ? "" : String(props.value)}
        onChange={(value) => props.onChange(value === "" ? null : value === "true")}
      >
        <HStack minH="40px" spacing={4}>
          <Radio isDisabled={props.isDisabled} value="true">
            Yes
          </Radio>
          <Radio isDisabled={props.isDisabled} value="false">
            No
          </Radio>
        </HStack>
      </RadioGroup>
    </FormControl>
  );
}

export function CaregiverEmergencyContactsGrid(props: { children: React.ReactNode }) {
  return (
    <Grid gap={4} templateColumns={{ base: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}>
      {props.children}
    </Grid>
  );
}

export function AddCaregiverEmergencyContactButton(props: {
  contactCount: number;
  isDisabled: boolean;
  onClick: () => void;
}) {
  return (
    <EntityModalButton
      disabled={props.isDisabled}
      iconClassName="fa fa-plus"
      kind="blue"
      size="sm"
      variant="outline"
      onClick={props.onClick}
    >
      {props.contactCount > 0 ? "Add another Emergency Contact" : "Add Emergency Contact"}
    </EntityModalButton>
  );
}

export function NoCaregiverEmergencyContactsRow(props: {
  isDisabled: boolean;
  isEditing: boolean;
  onAdd: () => void;
}) {
  return (
    <HStack
      borderBottom={entityProfileModalBorders.default}
      justifyContent="space-between"
      minH="56px"
    >
      <Text color={entityProfileModalColors.secondaryText} fontSize="14px" fontWeight={700}>
        No emergency contacts
      </Text>
      <Box minW="214px" textAlign="right">
        {props.isEditing ? (
          <AddCaregiverEmergencyContactButton
            contactCount={0}
            isDisabled={props.isDisabled}
            onClick={props.onAdd}
          />
        ) : null}
      </Box>
    </HStack>
  );
}
